// Labels, guidance and allowed actions for outreach inbox items
// (graham services/outreach/inbox.py). Kept pure so it is unit-tested.

import type { InboxItem, InboxSource, InboxStatus, InboxUiAction } from "../types/outreach.types"

export const SOURCE_LABEL: Record<string, string> = {
  outreach_reply: "Patient reply",
  outreach_missed_call: "Call did not go out",
  outreach_not_my_patient: "Not their practice / moved away",
  outreach_optout: "Opted out of texts",
  outreach_review: "Needs review",
  sms_unprocessed: "Text not processed",
}

/** The sources GET /outreach/org/{org}/inbox?source= accepts (anything else is a 400). */
export const INBOX_SOURCES: readonly InboxSource[] = [
  "outreach_reply",
  "outreach_missed_call",
  "outreach_not_my_patient",
  "outreach_optout",
  "outreach_review",
  "sms_unprocessed",
]

export const INBOX_STATUSES: readonly InboxStatus[] = ["pending", "completed", "all"]

export interface InboxQuery {
  org: string | null
  source: InboxSource | null
  status: InboxStatus
}

/**
 * Reads /outreach/inbox?org=&source=&status= (the weekly report email links
 * here). An unknown source is dropped rather than sent (the API would 400);
 * an unknown status falls back to pending.
 */
export function parseInboxQuery(params: URLSearchParams): InboxQuery {
  const org = params.get("org")?.trim() || null
  const rawSource = params.get("source")?.trim() || ""
  const rawStatus = params.get("status")?.trim().toLowerCase() || ""
  const source = (INBOX_SOURCES as readonly string[]).includes(rawSource) ? (rawSource as InboxSource) : null
  const status = (INBOX_STATUSES as readonly string[]).includes(rawStatus) ? (rawStatus as InboxStatus) : "pending"
  return { org, source, status }
}

/** Query params for GET /outreach/org/{org}/inbox: source only when set. */
export function inboxRequestParams(opts: { status?: InboxStatus; source?: InboxSource | null }): Record<string, string> {
  return { status: opts.status || "pending", ...(opts.source ? { source: opts.source } : {}) }
}

/** Review kinds the backend raises under outreach_review / outreach_reply, told apart by title. */
export type InboxKind = "join_by_text" | "shared_number" | "stuck" | null

export function inboxKind(item: Pick<InboxItem, "title">): InboxKind {
  const title = (item.title || "").toLowerCase()
  if (title.startsWith("wants to join by text")) return "join_by_text"
  if (title.startsWith("text from a shared number")) return "shared_number"
  if (title.startsWith("outreach is stuck")) return "stuck"
  return null
}

const KIND_LABEL: Record<Exclude<InboxKind, null>, string> = {
  join_by_text: "Wants to join by text",
  shared_number: "Shared phone number",
  stuck: "Outreach stuck",
}

/** The badge shown on an item: its specific kind when known, else its source. */
export function inboxLabel(item: Pick<InboxItem, "source" | "title">): string {
  const kind = inboxKind(item)
  if (kind) return KIND_LABEL[kind]
  return SOURCE_LABEL[item.source] || String(item.source || "Needs review").replace(/_/g, " ")
}

/** One sentence telling staff what happened and what is expected of them. */
export function inboxGuidance(item: Pick<InboxItem, "source" | "title" | "patient_id">): string | null {
  switch (inboxKind(item)) {
    case "join_by_text":
      return "The patient asked to join by text. Nothing was recorded: contact them to go over the program and record their answer."
    case "shared_number":
      return "Several patients share this phone number, so we could not tell who wrote. Contact them directly; this item can only be dismissed."
    case "stuck":
      return "The campaign tried to book this patient several times and was refused each time. Nothing happens until someone books, pauses or disenrolls them."
  }
  switch (item.source) {
    case "sms_unprocessed":
      return "We failed to process this text and the patient may not have had a proper answer. Read it and reply to the patient yourself."
    case "outreach_missed_call":
      return "A booked call was not placed when the patient was told. It was not re-booked automatically."
    case "outreach_not_my_patient":
      return "They said this isn't their practice or that they moved away. They have already been disenrolled and every call and text cancelled. If that is a mistake, re-enroll them; otherwise dismiss."
    case "outreach_optout":
      return "The patient texted STOP. Texts have stopped; calls are not affected."
  }
  if (!item.patient_id) return "This item is not linked to a patient; it can only be dismissed."
  return null
}

/**
 * Which one-click actions an item offers. None once the item is closed;
 * without a single patient only "dismiss". A "not my practice" patient was
 * already disenrolled by the system, so the only undo is re-enrolling.
 */
export function inboxActions(
  item: Pick<InboxItem, "source" | "title" | "patient_id"> & { status?: string | null },
): InboxUiAction[] {
  if (item.status && item.status.toLowerCase() !== "pending") return []
  if (!item.patient_id || inboxKind(item) === "shared_number") return ["dismiss"]
  if (item.source === "outreach_not_my_patient") return ["reenroll", "dismiss"]
  return ["book", "decline", "not_patient", "dismiss"]
}
